class YGImageLazyLoader extends HTMLElement {
  getLazyLoader() {
    if (!this._lazyLoader) {
      this._lazyLoader = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            const newImg = new Image();
            newImg.onload = () => {
              img.src = newImg.src;
              img.removeAttribute('data-src');
              observer.unobserve(img);
            };
            newImg.src = img.getAttribute('data-src');
          }
        });
      }, {
        rootMargin: "5px",
      });
    }
    return this._lazyLoader;
  }
}

class YGVideoLazyLoader extends HTMLElement {
  getLazyLoader() {
    if (!this._lazyLoader) {
      this._lazyLoader = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.playVideo()
            observer.unobserve(entry.target);
          }
        });
      }, {
        rootMargin: "50px",
      });
    }
    return this._lazyLoader;
  }
}

class YGVRFrame extends HTMLElement {
  constructor() {
    super();
    this._isIntersecting = false;
    this._iframe = null;
    this._syncFrame = null;
    this._observedNodes = [];
    this._scheduleSync = this.scheduleSync.bind(this);
    this._handleViewportChange = this.handleViewportChange.bind(this);
    this._handleMutations = this.handleMutations.bind(this);
    this._handleResize = this.handleResize.bind(this);
    this._handleVisibilityChange = this.handleVisibilityChange.bind(this);
  }

  connectedCallback() {
    if (this._connected) return;

    this._connected = true;
    this.setupObservers();
    this.scheduleSync();
  }

  disconnectedCallback() {
    this.teardownObservers();
    this.unmountIframe();
    this._connected = false;
  }

  requestLoad() {
    this.scheduleSync();
  }

  setupObservers() {
    this._viewportObserver = new IntersectionObserver(this._handleViewportChange, {
      threshold: [0, 0.01],
    });
    this._viewportObserver.observe(this);

    this._resizeObserver = new ResizeObserver(this._handleResize);
    this._resizeObserver.observe(this);

    this._mutationObserver = new MutationObserver(this._handleMutations);
    this._observedNodes = this.getObservedNodes();
    this._observedNodes.forEach((node) => {
      this._mutationObserver.observe(node, {
        attributes: true,
        attributeFilter: ['class', 'style', 'hidden', 'open', 'aria-hidden'],
      });
    });

    document.addEventListener('visibilitychange', this._handleVisibilityChange);
    window.addEventListener('scroll', this._scheduleSync, { passive: true });
    window.addEventListener('resize', this._scheduleSync, { passive: true });
    window.addEventListener('pageshow', this._scheduleSync);
  }

  teardownObservers() {
    this._viewportObserver?.disconnect();
    this._viewportObserver = null;

    this._resizeObserver?.disconnect();
    this._resizeObserver = null;

    this._mutationObserver?.disconnect();
    this._mutationObserver = null;
    this._observedNodes = [];

    document.removeEventListener('visibilitychange', this._handleVisibilityChange);
    window.removeEventListener('scroll', this._scheduleSync);
    window.removeEventListener('resize', this._scheduleSync);
    window.removeEventListener('pageshow', this._scheduleSync);

    if (this._syncFrame) {
      cancelAnimationFrame(this._syncFrame);
      this._syncFrame = null;
    }
  }

  getObservedNodes() {
    const nodes = [];
    let current = this;

    while (current && current.nodeType === Node.ELEMENT_NODE) {
      nodes.push(current);
      current = current.parentElement;
    }

    if (nodes[nodes.length - 1] !== document.documentElement) {
      nodes.push(document.documentElement);
    }

    return nodes;
  }

  handleViewportChange(entries) {
    const [entry] = entries;
    this._isIntersecting = !!entry?.isIntersecting && entry.intersectionRatio > 0;
    this.scheduleSync();
  }

  handleMutations() {
    this.scheduleSync();
  }

  handleResize() {
    this.scheduleSync();
  }

  handleVisibilityChange() {
    if (document.hidden) {
      this.unmountIframe();
      return;
    }

    this.scheduleSync();
  }

  scheduleSync() {
    if (this._syncFrame) {
      cancelAnimationFrame(this._syncFrame);
    }

    this._syncFrame = requestAnimationFrame(() => {
      this._syncFrame = null;
      this.syncIframe();
    });
  }

  syncIframe() {
    if (this.shouldLoad()) {
      this.mountIframe();
    } else {
      this.unmountIframe();
    }
  }

  shouldLoad() {
    if (!this.dataset.src || document.hidden) {
      return false;
    }

    if (!this.isActuallyVisible()) {
      return false;
    }

    const slide = this.closest('.swiper-slide');
    const slideWrapper = slide?.parentElement;

    if (slideWrapper) {
      const activeSlide = slideWrapper.querySelector('.swiper-slide-active');
      const visibleSlide = slideWrapper.querySelector('.swiper-slide-visible');
      const isActiveSlide = !!activeSlide && slide === activeSlide;

      if (visibleSlide && !isActiveSlide && !slide.classList.contains('swiper-slide-visible')) {
        return false;
      }

      if (!visibleSlide && activeSlide && slide !== activeSlide) {
        return false;
      }
    }

    if (!this.isInViewport() && !this._isIntersecting) {
      return false;
    }

    return true;
  }

  isActuallyVisible() {
    const rect = this.getBoundingClientRect();

    if (rect.width < 1 || rect.height < 1) {
      return false;
    }

    let current = this;

    while (current && current.nodeType === Node.ELEMENT_NODE) {
      if (current.hidden || current.getAttribute('aria-hidden') === 'true') {
        return false;
      }

      const style = window.getComputedStyle(current);

      if (
        style.display === 'none' ||
        style.visibility === 'hidden' ||
        style.visibility === 'collapse' ||
        style.contentVisibility === 'hidden'
      ) {
        return false;
      }

      if (current !== this && style.opacity === '0') {
        return false;
      }

      current = current.parentElement;
    }

    return true;
  }

  isInViewport() {
    const rect = this.getBoundingClientRect();
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    if (rect.width < 1 || rect.height < 1) {
      return false;
    }

    return (
      rect.bottom > 0 &&
      rect.right > 0 &&
      rect.top < viewportHeight &&
      rect.left < viewportWidth
    );
  }

  mountIframe() {
    if (this._iframe) return;

    const iframe = document.createElement('iframe');
    iframe.src = this.dataset.src;
    iframe.width = '100%';
    iframe.height = '100%';
    iframe.loading = 'eager';
    iframe.allow = 'microphone; camera';
    iframe.allowFullscreen = true;
    iframe.setAttribute('title', this.dataset.title || 'VR viewer');
    iframe.style.border = 'none';

    this.appendChild(iframe);
    this._iframe = iframe;
    this.setAttribute('loaded', '');
  }

  unmountIframe() {
    if (!this._iframe) return;

    this._iframe.removeAttribute('src');
    this._iframe.remove();
    this._iframe = null;
    this.removeAttribute('loaded');
  }
}

if (!customElements.get('yg-vr-frame')) {
  customElements.define('yg-vr-frame', YGVRFrame);
}

class YGUtils extends HTMLElement {

  debounce(func, delay) {
    let timeoutId;
    return function(...args) {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func.apply(this, args), delay);
    };
  }


  throttle(func, limit) {
    let inThrottle;
    return function(...args) {
      if (!inThrottle) {
        func.apply(this, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  }

  get #getCurrentSlidesPerView() {
    if (!this['breakpoints']) return 1;

    const screenWidth = window.innerWidth;


    const sortedKeys = Object.keys(this['breakpoints']).map(Number).sort((a, b) => a - b);

    let current = sortedKeys[0];


    for (let i = 0; i < sortedKeys.length; i++) {
      if (screenWidth >= sortedKeys[i]) {
        current = sortedKeys[i];
      } else {
        break;
      }
    }

    return this['breakpoints'][current].slidesPerView;
  }

  #getAllAttributes(dom) {
    let booleanAttrs = ['loop', 'pagination', 'navigation', 'scrollbar', 'centered-slides'];
    let jsonAttrs = ['breakpoints']
    for (const attr of dom.attributes) {
      if (attr.name.startsWith('data-')) {
        const cleanName = attr.name.slice(5);

        let value = attr.value;
        if (booleanAttrs.includes(cleanName)) {
          value = value === 'true';
        } else if (jsonAttrs.includes(cleanName)) {
          value = this.#parseSwiperBreakpoints(value);
        }

        this[cleanName] = value;
      }
    }
  }

  #parseSwiperBreakpoints(str) {
    const result = {};
    const pairs = str.split(',');

    for (const pair of pairs) {
      const [breakpoint, values] = pair.split(':');
      if (!breakpoint || !values) continue;

      const [slides, space] = values.split('-').map(Number);

      result[breakpoint.trim()] = {
        slidesPerView: slides,
        spaceBetween: space
      };
    }

    return result;
  }

  async initSwiper(dom, thumbnails) {
    const SwiperCtor = window.loadSwiper ? await window.loadSwiper() : window.Swiper;
    this.#getAllAttributes(dom || this);
    const swiper = new SwiperCtor(dom || this, {

      effect: this['effect'],
      loop: this['loop'],
      centeredSlides: this['centered-slides'],
      autoplay: this['autoplay']?{
        delay: this['autoplay'],
        stopOnLastSlide: false,
        disableOnInteraction: false,
        pauseOnMouseEnter: false,
      }:false,
      pagination: this['pagination']?{
        el: '.swiper-pagination',
        type: this['pagination-type']?this['pagination-type']:'bullets',
        clickable: true,
      }:false,
      navigation: this['navigation']?{
        nextEl: this['navigation-class']? `.${this['navigation-class']}-next`:'.swiper-button-next',
        prevEl: this['navigation-class']? `.${this['navigation-class']}-prev`:'.swiper-button-prev',
      }:false,
      breakpoints: this['breakpoints']?this['breakpoints']:false,
      on: {
        init: e => {
          if (e.passedParams.loop) {
            let num = this.#getCurrentSlidesPerView;
            let sliderLength = e.slides.length;

            if (this['centered-slides']) {
              num += 4;
            }

            if(sliderLength <= num){
              for (let index = 1; index < Math.ceil(num / sliderLength); index++) {
                e.slides.forEach((slide) => {
                  const clonedElement = slide.cloneNode(true);
                  e.slidesEl.appendChild(clonedElement);
                })
              }

              setTimeout(() => {
                let $bullets = this.querySelectorAll('.swiper-pagination-bullet');
                $bullets.forEach((bullet, index) => {
                  if (index >= sliderLength) {
                    bullet.style.display = 'none';
                  }
                })
              }, 250)
            }
          }
        }
      },
      thumbs: {
        swiper: thumbnails
      }
    })

    return swiper;
  }

  getLatestDom(requestUrlOrCallback, callback) {



    let requestUrl = requestUrlOrCallback;
    if (typeof requestUrlOrCallback === 'function') {
      callback = requestUrlOrCallback;
      requestUrl = window.location.href;
    } else {
      requestUrl = requestUrl || window.location.href;
    }

    this.abortController?.abort();
    this.abortController = new AbortController();

    const promise = fetch(requestUrl, {
      signal: this.abortController.signal,
    })
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        if (typeof callback === 'function') {
          callback(html);
        }
        return html;
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.log('Fetch aborted by user');
        } else {
          console.error(error);
        }

        throw error;
      });


    return promise;
  }
}

class YGSwiper extends YGUtils {
  constructor() {
    super();
    this.swiper = null;
  }
  connectedCallback() {

    if (this.swiper) return;

    const start = async () => {
      if (window.waitForElementVisible) {
        await window.waitForElementVisible(this);
      }

      this.swiper = await this.initSwiper(this);
    };

    start().catch((error) => {
      console.error('Failed to initialize yg-swiper:', error);
    });
  }
}
if (!customElements.get('yg-swiper')) {
  customElements.define('yg-swiper', YGSwiper);
}
